import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useFirestore } from 'vuefire'
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  type Unsubscribe,
} from 'firebase/firestore'
import type { TaskDirective } from '@/models/TaskDirective'
import { useProjectsStore } from '@/stores/projects'
import { trackAction } from '@/composables/useActivityTracker'
import { CsmAction } from '@/constants/csmActions'
import { logger } from '@/utils/logger'

export const useTaskDirectivesStore = defineStore('taskDirectives', () => {
  const directives = ref<TaskDirective[]>([])
  const loading = ref(false)
  let _unsub: Unsubscribe | null = null
  let _currentPid: string | null = null

  function subscribe(projectId?: string) {
    const pid = projectId ?? useProjectsStore().currentProjectId
    if (!pid) return

    if (_currentPid === pid && _unsub) return
    unsubscribe()
    _currentPid = pid
    loading.value = true

    const fs = useFirestore()
    _unsub = onSnapshot(
      collection(fs, `projects/${pid}/task_directives`),
      (snap) => {
        directives.value = snap.docs.map((d) => {
          const data = d.data()
          return {
            id: d.id,
            title: data.title ?? '',
            rule: data.rule ?? '',
            source_task_name: data.source_task_name ?? '',
            source_task_context: data.source_task_context ?? null,
            source_task_clauses: data.source_task_clauses ?? [],
            source_rejection_comment: data.source_rejection_comment ?? null,
            created_by_uid: data.created_by_uid ?? null,
            is_active: data.is_active ?? true,
            expires_at: data.expires_at?.toDate?.() ?? null,
            created_at: data.created_at?.toDate?.() ?? null,
            updated_at: data.updated_at?.toDate?.() ?? null,
          } as TaskDirective
        })
        loading.value = false
      },
      (error) => {
        logger.error('Error subscribing to task directives', error)
        loading.value = false
      },
    )
  }

  function unsubscribe() {
    _unsub?.()
    _unsub = null
    _currentPid = null
    directives.value = []
  }

  // Active-directive count after a mutation (the snapshot listener may not have
  // fired yet, so derive optimistically): all currently-active, minus the touched
  // one, plus it back if it ends up active.
  function activeCountAfter(directiveId: string, isActive: boolean): number {
    return directives.value.filter((d) => d.is_active && d.id !== directiveId).length + (isActive ? 1 : 0)
  }

  async function toggleActive(directiveId: string, isActive: boolean) {
    const pid = _currentPid ?? useProjectsStore().currentProjectId
    if (!pid) return
    try {
      const fs = useFirestore()
      await updateDoc(doc(fs, `projects/${pid}/task_directives/${directiveId}`), {
        is_active: isActive,
        updated_at: serverTimestamp(),
      })
      try {
        trackAction(CsmAction.DirectiveToggled, {
          projectId: pid,
          projectMeta: { directive_count: activeCountAfter(directiveId, isActive) },
        })
      } catch { /* best-effort */ }
    } catch (error) {
      logger.error('Error toggling directive', error)
      const { useAppStore } = await import('./app')
      useAppStore().toast('Error', 'Failed to update directive', 'error')
    }
  }

  async function updateExpiry(directiveId: string, expiresAt: Date | null) {
    const pid = _currentPid ?? useProjectsStore().currentProjectId
    if (!pid) return
    try {
      const fs = useFirestore()
      await updateDoc(doc(fs, `projects/${pid}/task_directives/${directiveId}`), {
        expires_at: expiresAt,
        updated_at: serverTimestamp(),
      })
    } catch (error) {
      logger.error('Error updating directive expiry', error)
      const { useAppStore } = await import('./app')
      useAppStore().toast('Error', 'Failed to update directive expiry', 'error')
    }
  }

  async function remove(directiveId: string) {
    const pid = _currentPid ?? useProjectsStore().currentProjectId
    if (!pid) return
    try {
      const fs = useFirestore()
      await deleteDoc(doc(fs, `projects/${pid}/task_directives/${directiveId}`))
      try {
        trackAction(CsmAction.DirectiveDeleted, {
          projectId: pid,
          projectMeta: { directive_count: activeCountAfter(directiveId, false) },
        })
      } catch { /* best-effort */ }
    } catch (error) {
      logger.error('Error deleting directive', error)
      const { useAppStore } = await import('./app')
      useAppStore().toast('Error', 'Failed to delete directive', 'error')
    }
  }

  return { directives, loading, subscribe, unsubscribe, toggleActive, updateExpiry, remove }
})
